import { useMemo, useState } from "react";
import { Loader2, Lock, ShieldHalf, Flame, Bell, Ban, KeyRound, Sparkles } from "lucide-react";
import { WhatsNewDialog } from "@/components/aph/WhatsNewDialog";
import { APP_VERSION } from "@/lib/changelog";
import { LoginKeyExport } from "@/components/aph/LoginKeyExport";
import { nameOf } from "@/lib/selectors";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVault } from "@/context/VaultContext";
import { WipeDialog } from "@/components/aph/WipeDialog";
import { InstallButton } from "@/components/InstallButton";

function DecoySetup() {
  const { setupDecoy } = useVault();
  const [alias, setAlias] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await setupDecoy(alias.trim(), pass);
      toast.success("Decoy vault sealed");
    } catch (e) {
      setError(e.message || "Could not create decoy vault");
    } finally {
      setBusy(false);
    }
  };
  const cls = "w-full bg-void border border-rim rounded-secure px-3 py-2 text-sm placeholder-ash/30 focus:outline-none focus:border-stellar/50";
  return (
    <div className="space-y-2.5">
      <input data-testid="settings-decoy-alias-input" value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Decoy alias" className={cls} />
      <input data-testid="settings-decoy-pass-input" type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Decoy passphrase (6+ chars)" className={cls} />
      {error && <p className="text-xs text-flare" data-testid="settings-decoy-error">{error}</p>}
      <button onClick={save} disabled={busy || !alias.trim() || pass.length < 6} data-testid="settings-decoy-save-btn" className="w-full flex items-center justify-center gap-2 border border-stellar/40 text-stellar rounded-secure py-2 text-sm disabled:opacity-40">
        {busy && <Loader2 size={14} className="animate-spin" />} Seal decoy vault
      </button>
    </div>
  );
}

function PushSetting() {
  const { pushSupported, enablePush, disablePush, profile } = useVault();
  const [on, setOn] = useState(pushSupported() && Notification.permission === "granted" && !!profile.push_on);
  const [busy, setBusy] = useState(false);
  if (!pushSupported()) return <p className="text-xs text-ash/45" data-testid="settings-push-unsupported">Push isn't available here yet. On iPhone, add Aphelion to your Home Screen first; the Android app gets push in a later update.</p>;
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); toast("Notifications off"); }
      else { await enablePush(); setOn(true); toast.success("Notifications on — content is never included"); }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid="settings-push-btn" data-state={on ? "on" : "off"} className={`w-full flex items-center justify-center gap-2 rounded-secure py-2 text-sm border transition-colors ${on ? "border-stellar/40 text-stellar" : "border-rim text-ash/70 hover:text-stellar"}`}>
      {busy && <Loader2 size={14} className="animate-spin" />} {on ? "Turn off notifications" : "Turn on notifications"}
    </button>
  );
}

function BlockedList() {
  const { q, version, unblockContact } = useVault();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const blocked = useMemo(() => q("SELECT * FROM contacts WHERE trust='blocked' ORDER BY alias"), [version]);
  if (!blocked.length) return <p className="text-xs text-ash/45" data-testid="blocked-empty">No blocked IDs.</p>;
  return (
    <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="blocked-list">
      {blocked.map((c) => (
        <div key={c.code} className="flex items-center justify-between gap-2 bg-nebula border border-glass rounded-secure px-3 py-2" data-testid={`blocked-item-${c.code.slice(4, 14)}`}>
          <div className="min-w-0"><p className="text-sm font-branding uppercase tracking-wider truncate">{nameOf(c)}</p><p className="font-mono text-[10px] text-ash/35 truncate">{c.code.slice(0, 24)}…</p></div>
          <button onClick={() => { unblockContact(c.code); toast("Unblocked — their next message arrives as a request"); }} data-testid={`unblock-btn-${c.code.slice(4, 14)}`} className="text-xs text-stellar border border-stellar/30 rounded-secure px-2.5 py-1.5 hover:shadow-stellar-glow">Unblock</button>
        </div>
      ))}
    </div>
  );
}

export function SettingsDialog({ open, onOpenChange }) {
  const { profile, lock } = useVault();
  const [wipeOpen, setWipeOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="settings-dialog" className="bg-nebula border-rim text-ash sm:max-w-md rounded-secure max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle className="font-branding">Vault settings</DialogTitle>
            <DialogDescription className="text-ash/50">Everything here lives only in this device's encrypted SQLite vault.</DialogDescription>
          </DialogHeader>
          <div className="bg-void border border-glass rounded-secure p-4">
            <p className="text-[11px] uppercase tracking-[0.16em] text-ash/40 font-branding">Identity</p>
            <p className="font-branding text-lg font-semibold uppercase tracking-wider" data-testid="settings-alias">{profile.alias}</p>
            <p className="font-mono text-[10px] text-ash/45 break-all mt-1">{profile.code}</p>
          </div>
          <div className="bg-void border border-glass rounded-secure p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2"><ShieldHalf size={16} strokeWidth={1.5} className="text-stellar" /> Decoy Vault</p>
            {profile.decoy_pin_active ? (
              <p className="text-xs text-ash/55" data-testid="settings-decoy-status">Configured. Its passphrase opens a separate identity indistinguishable from this one.</p>
            ) : (
              <>
                <p className="text-xs text-ash/55" data-testid="settings-decoy-status">Not configured. Creating one fills this device's second vault slot.</p>
                <DecoySetup />
              </>
            )}
          </div>
          <div className="bg-void border border-glass rounded-secure p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2"><KeyRound size={16} strokeWidth={1.5} className="text-stellar" /> Log in on another device</p>
            <p className="text-xs text-ash/55">Creates a passphrase-locked login key with your identity, contacts and groups. Nothing is uploaded.</p>
            <LoginKeyExport />
          </div>
          <div className="bg-void border border-glass rounded-secure p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2"><Bell size={16} strokeWidth={1.5} className="text-stellar" /> Notifications</p>
            <p className="text-xs text-ash/55">Alerts only say "New secure message". The relay never learns who sent it or what it says.</p>
            <PushSetting />
          </div>
          <div className="bg-void border border-glass rounded-secure p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2"><Ban size={16} strokeWidth={1.5} className="text-flare" /> Blocked IDs</p>
            <BlockedList />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { onOpenChange(false); lock(); }} data-testid="settings-lock-btn" className="flex-1 flex items-center justify-center gap-2 border border-rim rounded-secure py-2.5 text-sm hover:border-stellar/40 hover:text-stellar transition-colors"><Lock size={16} strokeWidth={1.5} /> Lock now</button>
            <button onClick={() => setWipeOpen(true)} data-testid="settings-wipe-btn" className="flex-1 flex items-center justify-center gap-2 border border-flare/40 text-flare rounded-secure py-2.5 text-sm hover:bg-flare/10 transition-colors"><Flame size={16} strokeWidth={1.5} /> Panic wipe</button>
          </div>
          <InstallButton className="w-full" />
          <button onClick={() => setNewsOpen(true)} data-testid="settings-whatsnew-btn" className="w-full flex items-center justify-center gap-2 border border-rim rounded-secure py-2.5 text-sm hover:border-stellar/40 hover:text-stellar transition-colors"><Sparkles size={16} strokeWidth={1.5} /> What's new · v{APP_VERSION}</button>
          <a href="/privacy" className="text-center text-xs text-ash/35 hover:text-ash/70" data-testid="settings-privacy-link">Privacy Policy</a>
        </DialogContent>
      </Dialog>
      <WipeDialog open={wipeOpen} onOpenChange={setWipeOpen} />
      {newsOpen && <WhatsNewDialog open={newsOpen} onOpenChange={setNewsOpen} />}
    </>
  );
}
